import { OverlayContainer } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_SORT_DEFAULT_OPTIONS } from '@angular/material/sort';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { InMemoryCache } from '@apollo/client/core';
import { loadErrorMessages } from '@apollo/client/dev';
import { PortalType, PortalTypeEnum } from '@ats/api/cm';
import {
	APOLLO_CACHE_CONFIG,
	CmCoreModule,
	CmErrorHandler,
	CmFormErrorService,
	CmViewportScroller,
} from '@ats/cm/core';
import { DEFAULT_PAGE_TITLE_KEY, DEFAULT_PAGE_TITLE_VALUE } from '@ats/cm/ui';
import { ApolloErrorEventHandler, apolloProvider } from '@ats/graphql';
import { IpCoreModule, IpUserService } from '@ats/ip/core';
import { IpUiModule } from '@ats/ip/ui';
import { AtsLoggerModule } from '@ats/logger';
import { AtsNotificationModule } from '@ats/notifications';
import { AtsStoreModule } from '@ats/store';
import { AtsOverlayContainer, AtsUiModule } from '@ats/ui';
import { AtsUtilityModule } from '@ats/utility';
import { CLIENT_SERVICE_CONFIG, ClientService, ClientServiceConfig } from '@cm/core/client';
import {
	CULTURE_SERVICE_CONFIG,
	CultureService,
	CultureServiceConfig,
	cultureInitializer,
} from '@cm/core/culture';
import { LocalizationModule } from '@cm/core/localization';
import { LOG_CONFIG, LogLevel, LoggingService } from '@cm/core/logging';
import { Environment, SETTINGS_CONFIG, SettingsConfig, SettingsService } from '@cm/core/settings';
import { STORAGE_DOMAIN } from '@cm/core/storage';
import { CmEventService, loadEventInitializer } from '@cm/core/system';
import { environment } from '@ip-env/environment';
import { provideApollo } from 'apollo-angular';
import { clientInitializer } from 'libs/core/client/src/lib/models';
import { IpRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

const apolloLoadEventHandler =
	(events: CmEventService) =>
	(handler = loadEventInitializer(events, 'apollo')): ApolloErrorEventHandler => ({
		apolloLoad: () => handler.onLoad(),
		apolloUnload: () => handler.onUnload(),
		apolloOnError: (error: any) => handler.onError(error),
	});

const cache = new InMemoryCache(APOLLO_CACHE_CONFIG);

const getEnvironment = (): Environment => {
	if (environment.production) {
		return 'production';
	} else if (environment.beta) {
		return 'beta';
	} else {
		return 'local';
	}
};

const portal = PortalType.InternalPortal;

if (environment.local) {
	loadErrorMessages();
}

const settingsConfig: SettingsConfig = {
	baseUrl: environment.apiUrl,
	cpUrl: environment.cpUrl,
	spUrl: environment.spUrl,
	environment: getEnvironment(),
	portal,
	env: {
		environment: getEnvironment(),
		...environment,
	},
};

const cultureConfig: CultureServiceConfig = {
	portal,
	api: environment.apiUrl,
};

const clientConfig: ClientServiceConfig = {
	portal,
};

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserAnimationsModule,
		BrowserModule,
		CmCoreModule.forRoot({
			api: environment.apiUrl,
			spUrl: environment.spUrl,
			cpUrl: environment.cpUrl,
			portal: PortalTypeEnum.InternalPortal,
		}),
		AtsStoreModule,
		AtsLoggerModule,
		AtsNotificationModule.forRoot(),
		RouterModule,
		IpCoreModule,
		IpRoutingModule,
		IpUiModule,
		AtsUiModule,
		AtsUtilityModule.forRoot({
			defaultDelayInMS:
				environment.processDelayInMS || environment.processDelayInMS === 0
					? environment.processDelayInMS
					: 7000,
		}),
		LocalizationModule.forRoot(),
	],
	exports: [LocalizationModule],
	providers: [
		{
			provide: SETTINGS_CONFIG,
			useValue: settingsConfig,
		},
		{
			provide: CULTURE_SERVICE_CONFIG,
			useValue: cultureConfig,
		},
		{
			provide: CLIENT_SERVICE_CONFIG,
			useValue: clientConfig,
		},
		provideAppInitializer(() => {
			const initializerFn = cultureInitializer(inject(CultureService));
			return initializerFn();
		}),
		provideAppInitializer(() => {
			const initializerFn = clientInitializer(inject(ClientService));
			return initializerFn();
		}),
		provideApollo(() => {
			const events = inject(CmEventService);
			return apolloProvider(
				apolloLoadEventHandler(events)(),
				environment.graphQLUrl,
				cache
			)();
		}),
		{ provide: OverlayContainer, useClass: AtsOverlayContainer },
		{
			provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
			useValue: {
				duration: 5000,
				horizontalPosition: 'right',
				verticalPosition: 'top',
			},
		},
		{
			provide: MAT_SORT_DEFAULT_OPTIONS,
			useValue: {
				disableClear: true,
			},
		},
		{
			provide: DEFAULT_PAGE_TITLE_KEY,
			useValue: 'Core/_Shared.Site.TabTitle.CompanyName',
		},
		{
			provide: DEFAULT_PAGE_TITLE_VALUE,
			useValue: 'CertMetrics',
		},
		{
			provide: STORAGE_DOMAIN,
			useValue: environment.storeDomain,
		},
		{
			provide: LOG_CONFIG,
			useValue: {
				logApi: `${environment.apiUrl}/log`,
				logLevel: LogLevel.Trace,
			},
		},
		{
			provide: ErrorHandler,
			useClass: CmErrorHandler,
			deps: [
				LoggingService,
				CmEventService,
				IpUserService,
				CmFormErrorService,
				SettingsService,
				Platform,
			],
		},
		{
			provide: ViewportScroller,
			useFactory: () => new CmViewportScroller('[cm-view-outlet]', inject(DOCUMENT), window),
		},
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
